import { BUILD_TIME } from "../buildInfo";
import React from "react";
import Card from "../components/ui/Card";

// User guide. The text follows the actual behavior of the app (periods and comparison from utils/date.js);
// when the logic changes, change it here as well.

const SECTIONS = [
  {
    id: "pocetak",
    title: "Prvo podešavanje",
    blocks: [
      {
        text: "Kad se aplikacija prvi put koristi, dovoljno je proći kroz ovih šest koraka. Posle toga se ništa ne mora podešavati ponovo.",
        steps: [
          ["Prijava", "upiši korisničko ime i lozinku. Ako nalog ima više firmi, izaberi firmu sa kojom radiš."],
          ["Profil", "klikni na svoje ime u gornjem desnom uglu: upiši Ime i Prezime, Email, dodaj sliku i, ako treba, promeni lozinku."],
          ["Aktivna firma", "na istoj stranici klikni Preuzmi podatke firme (Naziv, adresa, PIB, MB, žiro račun) i dodaj Logo firme. Logo firme se prikazuje samo u zaglavlju PDF-a i štampe, zajedno sa podacima. Logo u gornjem levom uglu aplikacije je fiksan."],
          ["Podešavanja, magacini", "izaberi podrazumevane magacine za Kontrolnu tablu i Promet artikala (polje Grafikoni / Tabele) i za Nabavku (polje Nabavka), pa klikni Sačuvaj."],
          ["Podešavanja, smene", "dodaj radne smene (najviše pet) sa početkom i krajem, pa klikni Sačuvaj. Bez smena kartica Trenutna smena broji od ponoći."],
          ["Kontakt", "podatke na stranici Kontakt upisuje administrator u fajl contact.json na serveru; korisnik ih ne menja u aplikaciji."],
        ],
      },
    ],
  },
  {
    id: "prijava",
    title: "Prijava i odjava",
    blocks: [
      {
        list: [
          ["Prijava", "korisničko ime i lozinka. Posle 10 pogrešnih pokušaja u 15 minuta prijava se privremeno zaključava, pa treba sačekati 15 minuta."],
          ["Izbor firme", "ako nalog ima više firmi, posle prijave se bira firma (lokacija). Firma se menja odjavom i ponovnom prijavom. Vidiš samo firme koje su dodeljene tvom nalogu."],
          ["Koliko traje prijava", "do 12 sati bez korišćenja aplikacije. Dok radiš, prijava se produžava sama. Kad istekne, aplikacija te vraća na prijavu."],
          ["Odjava", "ikonica za odjavu u gornjem desnom uglu potpuno prekida prijavu i briše lokalne podatke. Kad se odjaviš u jednom tabu, ostali tabovi se odjavljuju čim se na njih vratiš."],
          ["Tema", "dugme sa suncem i mesecem prebacuje svetlu i tamnu temu. Izbor se pamti na tom uređaju."],
          ["Meni", "na telefonu i uskom ekranu meni se otvara dugmetom u gornjem levom uglu. Ispod naslova stranice piše naziv aktivne firme."],
        ],
      },
    ],
  },
  {
    id: "pregled",
    title: "Kontrolna tabla",
    blocks: [
      {
        h: "Periodi",
        list: [
          ["Danas, Juče", "jedan dan."],
          ["Ova nedelja", "od ponedeljka do danas."],
          ["Ovaj mesec", "od prvog u mesecu do danas."],
          ["Prošli mesec", "ceo prethodni kalendarski mesec."],
          ["Prilagodi period", "izaberi Datum od i Datum do, pa klikni Primeni. Datum se bira iz kalendara, gde se mesec i godina biraju iz padajućih lista; ne kuca se."],
        ],
        after: "Desno od trake sa periodima piše tačan raspon datuma koji je trenutno prikazan.",
        fig: { src: "periodi.png", alt: "Traka sa periodima: Danas, Juče, Ova nedelja, Ovaj mesec, Prošli mesec i Prilagodi period", wide: true },
      },
      {
        h: "Magacin",
        text: "Padajuća lista Magacin ograničava prikaz na jedan ili više magacina (može se štiklirati više njih). Prazan izbor znači svi magacini. Podrazumevani izbor se zadaje u Podešavanjima. Magacin utiče na Promet, Broj računa, Prosečan račun, Rashod, Promet po satima, Plaćanja, Top 5 artikala, Promet i rashod po radnicima i na spiskove koji se otvaraju iz kartica. Ne utiče samo na Trenutnu smenu, koja je uvek za ceo lokal.",
      },
      {
        h: "Sa čim se period poredi",
        text: "Iznad kartica je prekidač Poređenje sa dve opcije: Prethodni period i Prošla godina. Izbor ne zavisi od izabranih datuma: ostaje isti kad promeniš period brzim izborom (Danas, Juče...) ili datumima, pa važi za sve kartice dok ga ne promeniš. Pored prekidača piše tačan raspon koji se poredi. Izbor se pamti u pregledaču. Kad je izabran Prethodni period, procenti ispod brojeva pokazuju promenu u odnosu na prethodni period, koji se bira prema izabranom periodu:",
        list: [
          ["Danas i Juče", "prethodni dan (za Danas je to juče, za Juče prekjuče)."],
          ["Ova nedelja", "ista nedelja pre 7 dana, isti raspon dana (ponedeljak do današnji dan naspram ponedeljka do istog dana prošle nedelje)."],
          ["Ovaj mesec", "isti datumi mesec dana unazad. Primer: 1.10. do 6.10. naspram 1.9. do 6.9."],
          ["Ceo kalendarski mesec", "prethodni ceo mesec. Primer: ceo mart naspram celog februara, bez obzira što mart ima 31, a februar 28 dana."],
          ["Prilagođen period", "ako obuhvata ceo kalendarski mesec, poredi se sa prethodnim celim mesecom, a u svim ostalim slučajevima pomera se oba datuma tačno mesec dana unazad. Ako ciljni mesec ima manje dana, uzima se njegov poslednji dan (31.3. postaje 28.2)."],
        ],
        after:
          "Promena se računa kao (trenutno minus prethodno) podeljeno sa prethodnim, u procentima. Ako nema šta da se poredi (prethodni period je imao nulu ili nema prometa), kartica uvek pokazuje sivo 0%, da se ne menja njen oblik. Za Rashod je rast loš, pa je obojen crveno, a pad zeleno.",
      },
      {
        h: "Poređenje sa prošlom godinom",
        text: "Isti period se poredi sa istim datumima pre godinu dana.",
        list: [
          ["Danas i Juče", "isti datum prošle godine (danas 7.10.2026. naspram 7.10.2025)."],
          ["Ova nedelja i Prilagođen period", "isti kalendarski datumi prošle godine. Dan u nedelji se može razlikovati, jer isti datum pada na drugi dan."],
          ["Ceo kalendarski mesec", "ceo isti mesec prošle godine. Primer: ceo februar 2028 (29 dana) naspram celog februara 2027 (28 dana)."],
          ["29. februar", "ako prošla godina nema 29. februar, uzima se 28. februar."],
        ],
      },
      {
        h: "Četiri kartice na vrhu i šta otvara klik",
        text: "Svaka kartica ispod broja pokazuje promenu u odnosu na izabrano poređenje. Kartice koje se mogu kliknuti otvaraju prozor sa detaljima; prozor se zatvara tasterom Escape ili klikom van njega.",
        list: [
          ["Promet", "zbir plaćanja po fiskalnim računima, storno računi se oduzimaju. Klik otvara prozor Računi u prometu: po jedan red za svaki račun sa PFR brojem, vremenom, stolom, konobarom i iznosom."],
          ["Broj računa", "broj fiskalnih računa u periodu. Kartica se ne klika, jer spisak računa već otvara kartica Promet."],
          ["Prosečan račun", "promet podeljen brojem računa. Klik otvara prozor sa dva reda: Prosek i Medijana. Medijana je račun u sredini kada se svi računi poređaju po iznosu (od 99 računa to je 50. po redu); ona je pouzdanija od proseka kad ima nekoliko vrlo velikih računa. Svaki red pokazuje promenu u odnosu na izabrano poređenje i datume sa kojima se poredi."],
          ["Rashod", "zbir rashoda iz kase. Klik otvara prozor Rashodi sa vremenom, stolom, konobarom i iznosom."],
        ],
        fig: { src: "kpi.png", alt: "Četiri kartice: Promet, Broj računa, Prosečan račun i Rashod, svaka sa promenom u odnosu na prethodni period", wide: true },
      },
      {
        h: "Spisak računa i rashoda",
        text: "Račun sa više načina plaćanja je jedan red sa zbirom. Klik na red otvara ispod njega stavke (artikal, količina, iznos) i načine plaćanja, bez novog prozora; ponovni klik ga zatvara. Kod rashoda klik na red otvara artikal i količinu. Pošto rashod nema svoj broj, ID se pravi kao datum i redni broj u tom danu, na primer 20261009-1 je prvi rashod 9.10.2026. Na jednoj strani je 25 stavki, a između strana se prelazi tasterima Prethodna i Sledeća. Ukupan broj i zbir se računaju jednom, na prvoj strani, pa su sledeće strane brže.",
        fig: { src: "racuni.png", alt: "Prozor Računi u prometu sa PFR brojem, vremenom, stolom, konobarom i iznosom, i stranicama na dnu" },
      },
      {
        h: "Grafikoni i kartice ispod",
        list: [
          ["Promet po satima", "stubići za svih 24 sata: vrednost i broj računa. Dugme Detalji otvara prozor Promet po danima sa dve kartice. Po danima: po jedan stubić za svaki dan perioda (vikendi su svetlije obojeni), isprekidana linija je prosek po danu, a blede stubiće daje period poređenja. Iznad grafikona su Najbolji dan, Najslabiji dan i Prosek po danu (računaju se dani sa prometom), a ispod je tabela po danima sa Excel, PDF i Štampa izvozom. Kada je izabran jedan dan (Danas, Juče), poredi se sa istim danom u prethodne 4 nedelje. Dan i sat: mapa 7 dana x 24 sata, jače obojeno polje je veći prosečan promet tog dana u nedelji i sata; klikom ili prelaskom miša vidi se iznos."],
          ["Plaćanja", "udeo načina plaćanja (gotovina, kartice...) sa iznosima i procentima u listi. Prati izbor Magacin kao i ostatak table."],
          ["Poređenje prometa", "tri reda: Ovaj period, Prethodni period (ili Prošla godina) i Razlika u dinarima, zelena kad je promet veći, crvena kad je manji."],
          ["Top 5 artikala", "pet artikala sa najvećom vrednošću prodaje u izabranom periodu, sa brojem komada. Dugme Detalji otvara Analizu prodaje: Prodaja po kategorijama (hrana, piće, ostalo, a zadržavanjem miša se vide tri najprodavanija artikla svake), Udeo po grupama artikala i Top 20 artikala sa količinom, vrednošću i udelom u prometu."],
          ["Promet i rashod po radnicima", "po jedna linija za svakog radnika, podeljena na promet i rashod. Broj desno je njihov zbir, a ispod linije piše koliko je promet, a koliko rashod. Prikazuje se prvih osam radnika, a ostali su navedeni kao \"i još N\"."],
        ],
      },
      {
        h: "Trenutna smena i zauzetost",
        text: "Trenutna smena je uvek prikazana kao tekuća smena, bez obzira na izabrani period i magacin. Smene se zadaju u Podešavanjima. Kartica pokazuje:",
        list: [
          ["Trenutno na stolovima", "zbir otvorenih (još ne naplaćenih) stavki na svim stolovima."],
          ["Zauzetost", "procenat zauzetih stolova."],
          ["Gotovina, Kartice, Rashod", "podaci od početka tekuće smene."],
          ["Aktivni konobari", "broj konobara koji su radili u smeni; zadržavanjem miša se vide imena."],
          ["Najbolji sto", "sto sa najvećim iznosom u smeni."],
        ],
        after: "Boje zauzetosti: ispod 35% zeleno (niska), od 35% do 65% zlatno (srednja), iznad 65% crveno (visoka). Iste boje se koriste svuda gde se prikazuje zauzetost, uključujući stranicu Zauzetost stolova.",
        fig: { src: "smena.png", alt: "Kartica Trenutna smena sa zauzetošću od 27 procenata prikazanom zelenom bojom" },
      },
      {
        h: "Osvežavanje i greške",
        text: "Podaci se učitavaju kad se otvori stranica ili promeni filter. Klik na već izabrani period (na primer Danas) ponovo učitava podatke. Ako neki deo ne uspe da se učita, prikazuje se poruka sa tasterom Pokušaj ponovo, a ostali delovi stranice rade normalno.",
      },
    ],
  },
  {
    id: "stolovi",
    title: "Zauzetost stolova",
    blocks: [
      {
        text: "Mapa ili lista svih stolova sa procentom zauzetosti, brojačima Zauzet i Slobodan i obojenim stanjem.",
        list: [
          ["Rejon", "bira deo lokala (na primer Prizemlje). Svi znači svi rejoni."],
          ["Status", "pokazuje zauzete, slobodne ili sve stolove. Brojači Zauzet i Slobodan uvek pokazuju stanje celog rejona, bez obzira na ovaj filter."],
          ["Prikaz", "Mapa pokazuje stolove na njihovim mestima, Lista kao spisak."],
          ["Klik na zauzet sto", "otvara prozor Sto sa stavkama (artikal, količina, cena, vrednost, tura), konobarom i redom Ukupno. Ako je sto zadužilo više konobara, svi su navedeni jedan za drugim, na primer Ana, Marko, Jelena."],
          ["Izvoz", "Excel, PDF i Štampa obuhvataju samo zauzete stolove izabranog rejona (vidi Izvoz i štampa). Dugmad su isključena kad nema zauzetih stolova."],
        ],
        fig: { src: "stolovi.png", alt: "Mapa stolova sa filterima Rejon i Status, brojačima Zauzet i Slobodan i obojenom zauzetošću", wide: true },
      },
    ],
  },
  {
    id: "promet-artikala",
    title: "Promet artikala",
    blocks: [
      {
        text: "Tabela prodaje po artiklima za izabrani period i magacine. Izaberi filtere i klikni Prikaži; tabela se ne menja dok se ne klikne Prikaži.",
        list: [
          ["Tip", "Objedinjeno (prodaja i rashod zajedno), Prodaja (izdati dokumenti prodaje) ili Rashod (rashod iz kase)."],
          ["Od, Do", "period; datum se bira iz kalendara."],
          ["Magacin", "jedan ili više magacina; prazan izbor znači svi."],
          ["Sortiranje", "klik na naslov kolone (Artikal, Količina, Vrednost) sortira tabelu, ponovni klik menja smer."],
          ["Ukupno", "na dnu je red Ukupno sa zbirom količine i vrednosti."],
        ],
        after: "Excel, PDF i Štampa koriste isti prikaz kao tabela i isključeni su kad nema podataka.",
      },
    ],
  },
  {
    id: "nabavka",
    title: "Nabavka",
    blocks: [
      {
        text: "Pregled potrošnje sirovina i predlog za naručivanje. Izaberi Od, Do, Magacini i (po želji) Dobavljače, pa klikni Prikaži.",
        list: [
          ["Lager", "trenutna količina u izabranim magacinima."],
          ["Potrošeno", "potrošnja u izabranom periodu."],
          ["Predlog", "izračunava se kao potrošeno minus lager i zaokružuje na prvu veću celu količinu (2,3 postaje 3). Ako je lager veći od potrošnje, predlog je nula. Polje Predlog (žuto) se menja ručno, a izmena ulazi u izvoz i štampu."],
          ["Dobavljač", "dobavljač od koga je artikal poslednji put nabavljen. Filter Dobavljači ostavlja samo njihove artikle."],
          ["Količine", "Lager i Potrošeno se prikazuju sa najviše 3 decimale, bez nepotrebnih nula (12.5000 je 12.5, a 12.34567 je 12.346)."],
          ["Izvor potrošnje", "Dokumenta pokazuju potrošnju iz dokumenata (kao do sada). Rashod (normativ) pokazuje samo rashod iz kase (storno) pretvoren u sirovine: rashod artikla sa normativom računa se kao količina puta količina svake sirovine u normativu, a rashod artikla bez normativa računa se direktno kao taj artikal. Dva izvora se nikad ne sabiraju; promena izvora odmah osvežava tabelu. Kod izvora Rashod (normativ) nema kolone Lager i ne poredi se sa stanjem: Predlog je jednostavno utrošena količina (možeš ga ručno ispraviti)."],
          ["Sakrij artikle bez prometa", "skriva artikle koji nemaju ni lager ni potrošnju."],
          ["Samo sa predlogom nabavke", "pokazuje samo artikle za koje je predlog veći od nule."],
        ],
        fig: { src: "nabavka.png", alt: "Tabela Nabavka sa kolonama Lager, Potrošeno, Predlog (žuto polje koje se menja) i Dobavljač", wide: true },
      },
    ],
  },
  {
    id: "izvoz",
    title: "Izvoz i štampa",
    blocks: [
      {
        text: "Na svakoj stranici sa tabelom postoje tri dugmeta: Excel, PDF i Štampa. Isključena su kad nema šta da se izveze. Excel, PDF i štampa dobijaju zaglavlje aktivne firme: PDF i štampa sa logom i podacima (Naziv, adresa, PIB, MB, žiro račun), a Excel samo sa tekstom firme iznad tabele. Logo i podaci firme se podešavaju na stranici Profil.",
        list: [
          ["Datum", "svaki PDF i štampa imaju datum i vreme štampe, a Excel datum izvoza."],
          ["Strane i logo", "na dnu svake strane PDF-a i štampe su broj strane (Strana 1 / 3) i logo aplikacije u donjem desnom uglu. Broj strane u štampi traži noviji Chrome, Edge ili Brave."],
          ["Excel", "svaka tabela ima podebljan red Ukupno koji sabira kolone sa brojevima. Brojevi ostaju brojevi, pa se u Excelu mogu dalje računati."],
          ["PDF", "preuzima se kao fajl; tekst sadrži sva slova srpskog jezika."],
          ["Naziv fajla", "Excel i PDF se čuvaju sa datumom i vremenom izvoza u nazivu (na primer lista_za_nabavku_2026-10-16_18-55.pdf), pa se ranije snimljeni fajlovi ne prepisuju."],
          ["Štampa", "otvara pregled za štampu u novom prozoru. Ako se ništa ne otvori, dozvoli iskačuće prozore (pop-up) za ovaj sajt."],
          ["Lista za nabavku", "PDF i štampa na stranici Nabavka daju Listu za nabavku: samo artikal i količina (predlog), sa rednim brojem i podvučenim redovima. Lager se ne štampa, a u listu ulaze samo artikli sa predlogom većim od nule. Excel nosi sve kolone: Lager, Potrošeno, Predlog i Dobavljač."],
          ["Stolovi", "izvoz i štampa na stranici Zauzetost stolova uvek obuhvataju samo zauzete stolove izabranog rejona, bez obzira na filter Status. Za svaki sto su stavke, konobar, vreme otvaranja (vreme prve stavke) i podebljan zbir stola, a na kraju zbir svih stolova."],
        ],
      },
    ],
  },
  {
    id: "profil",
    title: "Profil",
    blocks: [
      {
        text: "Profil se otvara klikom na svoje ime i sliku u gornjem desnom uglu. Sastoji se od četiri kartice.",
        fig: { src: "profil.png", alt: "Stranica Profil sa ličnim podacima, promenom lozinke i aktivnom firmom" },
      },
      {
        h: "Lični podaci",
        list: [
          ["Slika", "Dodaj sliku ili Zameni sliku: izaberi PNG, JPEG ili WebP. Aplikacija je sama smanji. Dugme Ukloni briše sliku. Slika se prikazuje u zaglavlju pored imena, a čuva se na serveru uz nalog, pa je vidiš na svakom uređaju."],
          ["Ime i Prezime", "najviše 50 karaktera."],
          ["Korisničko ime", "ne može da se menja."],
          ["Email", "po želji; mora biti ispravna adresa."],
          ["Sačuvaj", "čuva ime, prezime i email. Poruka Sačuvano potvrđuje čuvanje."],
        ],
      },
      {
        h: "Lozinka",
        text: "Dugme otvara prozor Promena lozinke: upiši Trenutnu lozinku, pa Novu lozinku (najmanje 8 karaktera) i ponovi je. Ako se nova i ponovljena ne poklapaju, ili trenutna nije tačna, poruka objašnjava šta ne valja. Posle 5 pogrešnih unosa trenutne lozinke promena se zaključava na 15 minuta.",
      },
      {
        h: "Aktivna firma",
        list: [
          ["Podaci firme", "Naziv, Adresa, Poštanski broj, Grad, Telefon, Žiro račun, Email, Web stranica, PIB i MB. Dolaze iz baze firme."],
          ["Preuzmi podatke firme", "ponovo čita podatke iz baze; koristi kad se podaci u kasi promene. Preuzeti podaci se pamte, pa izvoz i štampa ne čekaju na bazu."],
          ["Dodaj logo, Zameni logo, Ukloni", "logo firme se prikazuje samo u zaglavlju PDF-a i štampe. Logo u gornjem levom uglu aplikacije je fiksan i ne menja se. PNG logo sa providnom pozadinom ostaje providan."],
        ],
      },
      {
        h: "Licenca",
        text: "Kartica Licenca pokazuje datum isteka. Zelena oznaka Aktivna znači da licenca važi. Mesec dana pre isteka datum postaje narandžast kao upozorenje, a kad istekne oznaka postaje crvena Neaktivna.",
      },
    ],
  },
  {
    id: "podesavanja",
    title: "Podešavanja",
    blocks: [
      {
        list: [
          ["Podrazumevani magacin, Grafikoni / Tabele", "magacini koji su unapred izabrani kad se otvori Kontrolna tabla ili Promet artikala."],
          ["Podrazumevani magacin, Nabavka", "posebno polje, jer se za nabavku obično gleda magacin sirovina, a ne prodajni."],
          ["Sačuvaj", "čuva izbor magacina. Podešavanje se primenjuje pri sledećem otvaranju stranice."],
          ["Izgled", "izbor između Klasičan (uobičajeni izgled) i Kristal (staklaste, providne kartice sa zamućenom pozadinom). Radi u svetloj i u tamnoj temi i primenjuje se odmah. Izbor se pamti u tvom profilu, pa te prati i na drugom uređaju, a ako profil ne može da ga sačuva, pamti se u ovom pregledaču. Ako ti Kristal ne prija, vrati Klasičan i sve je kao ranije."],
          ["Radne smene", "do pet smena sa nazivom, početkom i krajem. Smena koja prelazi ponoć (22:00 do 02:00) prepoznaje se automatski: za kraj samo stavi vreme manje od početka. Dugme Dodaj smenu dodaje red, Ukloni ga briše, a Sačuvaj čuva sve smene odjednom."],
          ["Gde se smene koriste", "kartica Trenutna smena na Kontrolnoj tabli odlučuje po njima otkad da broji podatke. Ako nijedna smena ne odgovara trenutnom vremenu, broji se od ponoći."],
        ],
      },
    ],
  },
  {
    id: "kontakt",
    title: "Kontakt",
    blocks: [
      {
        text: "Stranica Kontakt ima dve kartice: podatke firme (naziv, adresa, grad, web i email) i Tehničku podršku (telefon i e-mail). Podatke upisuje administrator u fajl contact.json pored index.html na serveru, pa se menjaju bez nove verzije aplikacije. Dok nisu upisani, kartice pokazuju poruku o tome.",
      },
    ],
  },
  {
    id: "pravila",
    title: "Kako se računa",
    blocks: [
      {
        list: [
          ["Promet", "zbir plaćanja po fiskalnim računima (računi sa PFR brojem). Račun tipa Request se ne računa."],
          ["Storno", "storno računi se oduzimaju, i u prometu i u spisku računa i stavki."],
          ["Prosečan račun", "promet podeljen brojem računa."],
          ["Medijana", "srednji račun po iznosu; za paran broj računa prosek dva srednja."],
          ["Rashod", "stavke rashoda iz kase (tip s)."],
          ["Zauzetost", "zauzeti stolovi podeljeni sa svim stolovima."],
          ["Datumi", "period uključuje ceo prvi i ceo poslednji dan."],
        ],
      },
    ],
  },
  {
    id: "problemi",
    title: "Kad nešto ne radi",
    blocks: [
      {
        list: [
          ["Vraća na prijavu", "prijava je istekla (12 sati bez korišćenja), odjavljen si u drugom tabu ili je administrator prekinuo pristup. Prijavi se ponovo."],
          ["Previše pokušaja", "pogrešna lozinka više od 10 puta u 15 minuta zaključava prijavu; sačekaj 15 minuta."],
          ["Podaci nisu učitani", "klikni Pokušaj ponovo. Ako se ponavlja, proveri vezu sa internetom pa javi administratoru (stranica Kontakt)."],
          ["Nema podataka", "proveri period i izbor Magacin; prazan izbor znači svi magacini."],
          ["Brojevi se ne poklapaju", "Promet ne uključuje račune tipa Request i oduzima storno. Trenutna smena ne zavisi od izbora Magacin."],
          ["Dugmad za izvoz su siva", "nema podataka za izvoz. Na stranici Nabavka PDF i Štampa su sivi i kad nema artikala sa predlogom."],
          ["Štampa se ne otvara", "dozvoli iskačuće prozore za ovaj sajt u pregledaču."],
          ["Nema logoa u izvozu", "dodaj Logo firme u Profilu (Aktivna firma) i klikni Preuzmi podatke firme."],
          ["Stara verzija stranice", "osveži stranicu tasterima Ctrl i F5. Dole na ovoj stranici piše datum verzije aplikacije koju vidiš."],
        ],
      },
    ],
  },
];

function Figure({ fig, className = "" }) {
  return (
    <figure className={className}>
      <img
        src={`${process.env.PUBLIC_URL || ""}/help-slike/${fig.src}`}
        alt={fig.alt}
        loading="lazy"
        className="w-full rounded-lg border border-slate-200 dark:border-slate-700"
      />
    </figure>
  );
}

function Block({ block }) {
  const body = (
    <div className="flex min-w-0 flex-col gap-2">
      {block.h && <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{block.h}</h4>}
      {block.text && <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{block.text}</p>}
      {block.steps && (
        <ol className="flex flex-col gap-2">
          {block.steps.map(([term, desc], i) => (
            <li key={term} className="flex gap-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100"
              >
                {i + 1}
              </span>
              <span>
                <span className="font-medium text-slate-900 dark:text-slate-50">{term}:</span> {desc}
              </span>
            </li>
          ))}
        </ol>
      )}
      {block.list && (
        <ul className="flex flex-col gap-1.5">
          {block.list.map(([term, desc]) => (
            <li key={term} className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              <span className="font-medium text-slate-900 dark:text-slate-50">{term}:</span> {desc}
            </li>
          ))}
        </ul>
      )}
      {block.after && <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{block.after}</p>}
    </div>
  );

  if (!block.fig) return body;
  // Wide images (strips, tables) go below the text; narrow ones (cards, dialogs) go beside the text on a larger screen.
  if (block.fig.wide) {
    return (
      <div className="flex flex-col gap-3">
        {body}
        <Figure fig={block.fig} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,19rem)] md:items-start">
      {body}
      <Figure fig={block.fig} />
    </div>
  );
}

export default function Help() {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <nav aria-label="Sadržaj" className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-surface px-3 text-xs font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {s.title}
          </a>
        ))}
      </nav>

      {SECTIONS.map((s) => (
        <Card key={s.id} title={s.title} className="scroll-mt-4">
          <div id={s.id} className="flex scroll-mt-20 flex-col gap-4">
            {s.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>
        </Card>
      ))}

      <p className="text-center text-xs text-slate-500 dark:text-slate-400">
        Verzija aplikacije: {BUILD_TIME ? new Date(BUILD_TIME).toLocaleString("sr-RS", { dateStyle: "short", timeStyle: "short" }) : "razvojna"}
      </p>
    </div>
  );
}
